import { useRef, useState } from 'react'
import { copyText, shareText } from '../lib/clipboard'
import { restoreLink } from '../lib/invite'
import { isInsideMax, shareInvite } from '../max'
import { T } from '../strings'
import type { Theme } from '../theme'
import type { GameState, Profile as ProfileData } from '../game/types'
import type { GameStore } from '../game/store'
import Toast from '../components/Toast'

interface Props {
  state: GameState
  theme: Theme
  store: GameStore
  onChangeProfile: (patch: Partial<ProfileData>) => void
}

export default function Profile({ state, theme, store, onChangeProfile }: Props) {
  const fileRef = useRef<HTMLInputElement>(null)
  const [note, setNote] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  /** Ввод чужого ключа спрятан: он нужен раз в жизни, а место занимает всегда. */
  const [entering, setEntering] = useState(false)
  const [typed, setTyped] = useState('')
  const { profile, account, restoreAsk, restoreNote } = state
  const isMax = account?.kind === 'max'
  const isPhone = account?.kind === 'phone'
  const insideMax = isInsideMax()
  const key = account?.restoreCode ?? ''

  /** Все действия с сервером ведут себя одинаково: блокировка, затем текст-результат. */
  const run = async (action: () => Promise<string | null>, okText: string | null = null) => {
    setBusy(true)
    setNote(null)
    const error = await action()
    setBusy(false)
    setNote(error ?? okText)
  }

  const pickPhoto = (file: File | undefined) => {
    if (!file) return
    void run(() => store.uploadAvatar(file))
  }

  /** Ключ уходит родителям и текстом, и ссылкой: мессенджеры режут по-разному. */
  const sendKey = async () => {
    const text = T.keyShare(key)
    const link = restoreLink(key)

    if (await shareInvite(text, link)) return
    if (await shareText(text, link)) return
    setNote((await copyText(`${text}\n${link}`)) ? T.keyCopied : T.copyFailed)
  }

  /**
   * Набранный руками ключ идёт через то же подтверждение, что и ссылка:
   * переключение аккаунта необратимо, а ключ могли дать чужой — например,
   * брата. Поле закрываем только когда сервер ключ узнал.
   */
  const enterKey = async () => {
    const code = typed.trim()
    if (!code || busy) return

    setBusy(true)
    setNote(null)
    const error = await store.peekRestore(code)
    setBusy(false)
    setNote(error)
    if (!error) {
      setTyped('')
      setEntering(false)
    }
  }

  const syncNote =
    state.sync === 'saving' ? T.syncSaving : state.sync === 'offline' ? T.syncOffline : T.syncIdle

  return (
    <div className="screen screen--pad">
      <h1 className="screen__title">{T.profileTitle}</h1>

      {restoreAsk && (
        <div className="keycard keycard--ask" style={{ borderColor: theme.acc }}>
          <div className="keycard__title">{T.keyAskTitle}</div>
          <div className="keycard__who">
            {restoreAsk.name} · {T.chooseLearned(restoreAsk.pct)}
          </div>
          <button
            type="button"
            className="btn-3d btn-scale keycard__btn"
            disabled={busy}
            style={{ background: theme.acc, color: theme.onacc }}
            onClick={() => void run(() => store.restore(restoreAsk.code), T.keyWelcome(restoreAsk.name))}
          >
            {T.keyEnter}
          </button>
          <button type="button" className="keycard__later" onClick={store.dismissRestoreAsk}>
            {T.keyLater}
          </button>
        </div>
      )}

      <div className="profile__head">
        <div className="profile__photo" style={{ borderColor: theme.line }}>
          {profile.avatarUrl ? <img src={profile.avatarUrl} alt="" /> : <span aria-hidden>🙂</span>}
        </div>
        <div className="profile__photo-actions">
          <button
            type="button"
            className="btn-ghost btn-scale profile__photo-btn"
            disabled={busy}
            onClick={() => fileRef.current?.click()}
          >
            {T.changePhoto}
          </button>
          {profile.avatarUrl && (
            <button
              type="button"
              className="btn-ghost btn-scale profile__photo-btn"
              disabled={busy}
              onClick={() => void run(() => store.removeAvatar())}
            >
              {T.removePhoto}
            </button>
          )}
        </div>
        <input
          ref={fileRef}
          type="file"
          accept="image/*"
          hidden
          onChange={(e) => {
            pickPhoto(e.target.files?.[0])
            e.target.value = ''
          }}
        />
      </div>

      <div className="fields">
        <label className="field">
          <span className="field__label">{T.firstName}</span>
          <input
            className="input"
            type="text"
            value={profile.firstName}
            maxLength={40}
            onChange={(e) => onChangeProfile({ firstName: e.target.value })}
          />
        </label>
        <label className="field">
          <span className="field__label">{T.lastName}</span>
          <input
            className="input"
            type="text"
            value={profile.lastName}
            maxLength={40}
            onChange={(e) => onChangeProfile({ lastName: e.target.value })}
          />
        </label>
        <label className="field">
          <span className="field__label">{T.age}</span>
          <input
            className="input"
            type="number"
            inputMode="numeric"
            min={4}
            max={18}
            value={profile.age ?? ''}
            onChange={(e) => {
              const v = e.target.value
              onChangeProfile({ age: v === '' ? null : Number(v) })
            }}
          />
        </label>
      </div>

      <div className="keycard" style={{ borderColor: theme.acc }}>
        <div className="keycard__hero" aria-hidden>
          🔑
        </div>
        <div className="keycard__title">{T.keyTitle}</div>
        <div className="keycard__why">{T.keyWhy}</div>
        <div className="keycard__code" style={{ color: theme.acc, borderColor: theme.line }}>
          {key || '······'}
        </div>
        <button
          type="button"
          className="btn-3d btn-scale keycard__btn"
          disabled={!key || busy}
          style={{ background: theme.acc, color: theme.onacc }}
          onClick={() => void sendKey()}
        >
          📤 {T.keySend}
        </button>
        <button
          type="button"
          className="btn-ghost btn-scale keycard__btn"
          disabled={!key}
          onClick={() => void copyText(key).then((ok) => setNote(ok ? T.keyCopied : T.copyFailed))}
        >
          📋 {T.keyCopy}
        </button>
        <div className="keycard__secret" style={{ color: theme.warn }}>
          {T.keySecret}
        </div>
      </div>

      {/* Внутри MAX ключ вводить нечего: аккаунт и так свой, а восстановленный
          при следующем запуске поглотит автовход. */}
      {!insideMax &&
        (entering ? (
          <div className="add-friend">
            <div className="invite__step">{T.keyHaveStep}</div>
            <div className="add-friend__row">
              <input
                className="input"
                type="text"
                value={typed}
                placeholder={T.keyPlaceholder}
                maxLength={24}
                autoCapitalize="characters"
                autoCorrect="off"
                spellCheck={false}
                onChange={(e) => setTyped(e.target.value.toUpperCase())}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') void enterKey()
                }}
              />
              <button
                type="button"
                className="btn-3d btn-scale"
                disabled={busy || !typed.trim()}
                style={{ background: theme.acc2 }}
                onClick={() => void enterKey()}
              >
                {T.keyEnter}
              </button>
            </div>
          </div>
        ) : (
          <button type="button" className="name-hint" onClick={() => setEntering(true)}>
            {T.keyHave}
          </button>
        ))}

      {insideMax && account?.maxEnabled && !isMax && (
        <button
          type="button"
          className="btn-3d max-btn"
          disabled={busy}
          style={{ background: theme.acc2 }}
          onClick={() => void run(() => store.loginWithMax())}
        >
          {T.loginMax}
        </button>
      )}

      {isMax && (
        <div className="profile__max">
          <div className="profile__ok">✓ {T.loggedInMax}</div>
          {account?.hasPhone ? (
            <div className="profile__hint">{T.phoneLinked}</div>
          ) : (
            <button
              type="button"
              className="btn-3d max-btn"
              disabled={busy}
              style={{ background: theme.acc2 }}
              onClick={() => void run(() => store.linkPhone(), T.phoneLinked)}
            >
              {T.sharePhone}
            </button>
          )}
        </div>
      )}

      {isPhone && (
        <div className="profile__max">
          <div className="profile__ok">✓ {T.loggedInPhone}</div>
          <div className="profile__hint">{T.phoneLinked}</div>
        </div>
      )}

      <Toast
        text={note ?? restoreNote}
        onHide={() => {
          setNote(null)
          store.dismissRestoreNote()
        }}
      />

      <div className="profile__sync">{syncNote}</div>
    </div>
  )
}
